import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { avisoAltaConOrigen } from './PatientFormDialog';
import CampoComoNosConocio from './CampoComoNosConocio';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Lightweight dialog to create a patient inline (without leaving the current
 * flow, e.g. while creating a budget). Returns the newly created patient via
 * `onCreated(patient)` so the parent can immediately select them.
 *
 * Pre-fills `name`, `surname1`, `surname2` from an `initialName` prop —
 * handy when the user clicked on an unmatched name from the day's agenda.
 *
 * Pide los MISMOS cuatro datos que la ficha de Pacientes (nombre, dos
 * apellidos y teléfono, 2026-09-11) y con el mismo aviso: el
 * servidor los exige igual, así que antes esto dejaba pulsar «Crear
 * paciente» para contestar con un 400 — el usuario se enteraba tarde.
 * «Rápido» es que no haya que salirse del presupuesto, no que falten datos.
 *
 * Y «¿Cómo nos conoció?», obligatorio en toda alta desde el 23/9/2026
 * (decisión de dirección): el servidor lo exige igual, así que aquí también.
 */
export function QuickNewPatientDialog({ open, onOpenChange, onCreated, initialName = '' }) {
  const [form, setForm] = useState({ name: '', surname1: '', surname2: '', nif: '', phone: '', how_known_us: '' });
  const [busy, setBusy] = useState(false);
  const [origenTocado, setOrigenTocado] = useState(false);

  // Pre-fill from initialName when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setOrigenTocado(false);
    const tokens = (initialName || '').trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
      setForm({ name: '', surname1: '', surname2: '', nif: '', phone: '', how_known_us: '' });
    } else if (tokens.length === 1) {
      setForm({ name: tokens[0], surname1: '', surname2: '', nif: '', phone: '', how_known_us: '' });
    } else if (tokens.length === 2) {
      setForm({ name: tokens[0], surname1: tokens[1], surname2: '', nif: '', phone: '', how_known_us: '' });
    } else {
      // Heuristic: assume "Nombre [Compuesto] Apellido1 Apellido2".
      // For 3 tokens -> Nombre · Apellido1 · Apellido2.
      // For 4+ -> first one is name, second-to-last is surname1, last is surname2.
      const name = tokens[0];
      const surname2 = tokens[tokens.length - 1];
      const surname1 = tokens[tokens.length - 2];
      setForm({ name, surname1, surname2, nif: '', phone: '', how_known_us: '' });
    }
  }, [open, initialName]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const aviso = useMemo(() => avisoAltaConOrigen(form), [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (aviso) {
      setOrigenTocado(true);
      toast.error(aviso);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        surname1: form.surname1.trim() || null,
        surname2: form.surname2.trim() || null,
        nif: form.nif.trim() || null,
        phone: form.phone.trim() || null,
        how_known_us: form.how_known_us,
      };
      const res = await api.post('/api/patients', payload);
      toast.success('Paciente creado');
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo crear el paciente');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-new-patient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#13231E]" />
            Crear paciente rápido
          </DialogTitle>
        </DialogHeader>
        {/* noValidate: el aviso lo damos nosotros, entero y en cristiano. */}
        <form onSubmit={submit} noValidate className="space-y-3">
          <div className="text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] p-3 rounded">
            Crea un paciente con los datos mínimos para poder generar el presupuesto. Podrás completar el resto (NIF, dirección, etc.) más tarde desde la sección Pacientes.
          </div>
          <div>
            <Label>Nombre <span className="text-red-600">*</span></Label>
            <Input
              autoFocus
              value={form.name}
              onChange={(e) => setF('name', e.target.value)}
              data-testid="quick-patient-name"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Primer apellido <span className="text-red-600">*</span></Label>
              <Input
                value={form.surname1}
                onChange={(e) => setF('surname1', e.target.value)}
                data-testid="quick-patient-surname1"
                required
              />
            </div>
            <div>
              <Label>Segundo apellido <span className="text-red-600">*</span></Label>
              <Input
                value={form.surname2}
                onChange={(e) => setF('surname2', e.target.value)}
                data-testid="quick-patient-surname2"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>NIF</Label>
              <Input
                value={form.nif}
                onChange={(e) => setF('nif', e.target.value)}
                placeholder="12345678X"
                data-testid="quick-patient-nif"
              />
            </div>
            <div>
              <Label>Teléfono <span className="text-red-600">*</span></Label>
              <Input
                value={form.phone}
                onChange={(e) => setF('phone', e.target.value)}
                placeholder="600000000"
                data-testid="quick-patient-phone"
                required
              />
            </div>
          </div>
          <CampoComoNosConocio
            valor={form.how_known_us}
            onChange={(v) => setF('how_known_us', v)}
            onBlur={() => setOrigenTocado(true)}
            tocado={origenTocado}
            id="quick-patient-how-known-us"
            testid="quick-patient-how-known-us"
          />
          {/* Qué falta, antes de pulsar: el botón está apagado y sin esto no
              se sabe por qué. */}
          {aviso && (
            <div className="text-xs text-red-600" data-testid="quick-patient-faltan">{aviso}</div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="quick-patient-cancel">
              Cancelar
            </Button>
            <Button type="submit" disabled={busy || !!aviso} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="quick-patient-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando…</> : 'Crear paciente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
